import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  InventoryBinLocationCreateSchema,
  InventoryBinLocationQueryAllSchema,
  InventoryBinLocationQuerySchema,
  InventoryBinLocationUpdateSchema,
} from './schemas';

export const useGetInventoryBinLocationMutation: UseControlledMutation<InventoryBinLocationQuerySchema> = () =>
  useMutation({
    mutationFn: async ({ search, per_page, include }) => {
      return queryClient.fetchQuery({
        queryKey: ['INVENTORY_BIN_LOCATION_ASYNC', { search, per_page, include }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<InventoryBinLocationQueryAllSchema>({
            endpoint: '/api/v1/bin-locations',
            params: { search, per_page, include },
          });

          return orderBy(res?.data ?? [], ['location']) || [];
        },
      });
    },
  });

export function useGetInventoryBinLocations(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['INVENTORY_BIN_LOCATIONS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<InventoryBinLocationQueryAllSchema>({
        endpoint: '/api/v1/bin-locations?include=',
        params: `${apiParamStr}`,
      });
      return res;
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['INVENTORY_BIN_LOCATIONS'] });
};

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, InventoryBinLocationCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/bin-locations/',
        payload,
      }),
  });

export function useGetInventoryBinLocation(id: string) {
  return useSuspenseQuery({
    queryKey: ['INVENTORY_BIN_LOCATION', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: InventoryBinLocationQuerySchema }>({
        endpoint: `/api/v1/bin-locations/${id}`,
        // params: { include: 'terms_conditions' },
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: InventoryBinLocationQuerySchema }, ApiError, Partial<InventoryBinLocationUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/bin-locations/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['INVENTORY_BIN_LOCATIONS_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/bin-locations/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
